import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, AlertCircle, PenSquare, Plus, Pin, Bell, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { adminNoticeApi, AdminNoticeListItem } from '@/api/noticeApi';

export default function AdminNoticeList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<AdminNoticeListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const PAGE_SIZE = 15;

  const loadList = (page: number) => {
    setLoading(true);
    setError(null);
    adminNoticeApi.getList(page, PAGE_SIZE)
      .then(res => {
        setItems(res.data.content ?? []);
        setTotalPages(res.data.totalPages ?? 0);
        setTotalElements(res.data.totalElements ?? 0);
        setCurrentPage(res.data.number ?? page);
      })
      .catch(() => setError('공지사항 목록을 불러오는데 실패했습니다.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadList(0); }, []);

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('공지사항을 삭제하시겠습니까?')) return;
    setDeletingId(id);
    try {
      await adminNoticeApi.delete(id);
      loadList(currentPage);
    } catch {
      setError('삭제에 실패했습니다.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleTogglePin = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await adminNoticeApi.togglePin(id);
      loadList(currentPage);
    } catch {
      setError('상단고정 변경에 실패했습니다.');
    }
  };

  const handleToggleBanner = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await adminNoticeApi.toggleBanner(id);
      loadList(currentPage);
    } catch {
      setError('배너 변경에 실패했습니다.');
    }
  };

  const formatDate = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString('ko-KR') : '-';

  return (
    <div className="space-y-6 p-4 md:p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">공지사항 관리</h1>
          <p className="text-sm text-gray-500 mt-0.5">공지사항을 작성하고 관리합니다.</p>
        </div>
        <Button onClick={() => navigate('/dashboard/admin/notices/new')}>
          <Plus className="w-4 h-4 mr-2" />
          공지 작성
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium text-gray-700">
            전체 {(totalElements ?? 0).toLocaleString()}건
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-7 h-7 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-16 text-center whitespace-nowrap">번호</TableHead>
                  <TableHead>제목</TableHead>
                  <TableHead className="w-20 text-center">고정</TableHead>
                  <TableHead className="w-20 text-center">배너</TableHead>
                  <TableHead className="w-20 text-center">조회수</TableHead>
                  <TableHead className="w-28 text-center">등록일</TableHead>
                  <TableHead className="w-24 text-center">관리</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-400 py-16">
                      등록된 공지사항이 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, idx) => (
                  <TableRow
                    key={item.id}
                    className={`cursor-pointer hover:bg-blue-50/50 transition-colors ${item.isPinned ? 'bg-blue-50/30' : ''}`}
                    onClick={() => navigate(`/dashboard/admin/notices/${item.id}/edit`)}
                  >
                    <TableCell className="text-center text-gray-400 text-sm">
                      {totalElements - (currentPage * PAGE_SIZE + idx)}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-gray-800 line-clamp-1">{item.title}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <button
                        onClick={e => handleTogglePin(item.id, e)}
                        className={`p-1 rounded transition-colors ${item.isPinned ? 'text-blue-600 hover:bg-blue-100' : 'text-gray-300 hover:text-blue-400 hover:bg-blue-50'}`}
                        title={item.isPinned ? '고정 해제' : '상단 고정'}
                      >
                        <Pin className="w-4 h-4" />
                      </button>
                    </TableCell>
                    <TableCell className="text-center">
                      <button
                        onClick={e => handleToggleBanner(item.id, e)}
                        className={`p-1 rounded transition-colors ${item.isBanner ? 'text-orange-500 hover:bg-orange-100' : 'text-gray-300 hover:text-orange-400 hover:bg-orange-50'}`}
                        title={item.isBanner ? '배너 해제' : '배너 설정'}
                      >
                        <Bell className="w-4 h-4" />
                      </button>
                    </TableCell>
                    <TableCell className="text-center text-sm text-gray-500">
                      {(item.viewCount ?? 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-center text-sm text-gray-500">
                      {formatDate(item.createdAt)}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={e => { e.stopPropagation(); navigate(`/dashboard/admin/notices/${item.id}/edit`); }}
                          className="h-7 w-7 p-0 text-gray-500 hover:text-primary"
                        >
                          <PenSquare className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={e => handleDelete(item.id, e)}
                          disabled={deletingId === item.id}
                          className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                        >
                          {deletingId === item.id
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Trash2 className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-1">
          <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => loadList(currentPage - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          {Array.from({ length: totalPages }, (_, i) => (
            <Button
              key={i}
              variant={i === currentPage ? 'default' : 'outline'}
              size="sm" className="w-9"
              onClick={() => loadList(i)}
            >
              {i + 1}
            </Button>
          ))}
          <Button variant="outline" size="sm" disabled={currentPage === totalPages - 1} onClick={() => loadList(currentPage + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
